import type { ReconcileRxChangeRequestT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { useReconcileRxChangeMutation } from '@/entities/rx';
import { FormField } from '@/shared/ui/primitives';

import { reconcileDraftSchema, type ReconcileDraft } from '../model/schemas';
import { useDetailAction } from '../model/useDetailAction';
import { DetailActionForm } from './DetailActionForm';

export interface RxChangeReconcileFormProps {
  readonly rxChangeId: number;
  /** Called after a successful reconcile (e.g. to close the parent sheet). */
  readonly onCompleted?: () => void;
}

const DEFAULT_VALUES: ReconcileDraft = {
  userId: '',
  referencedPrescriptionId: '',
};

/**
 * Link an RxChange to an existing prescription on the patient's record.
 * ``userId`` is the patient's lotushealth user UUID (path param);
 * ``referencedPrescriptionId`` is the DoseSpot prescription id (body).
 * Mutation success invalidates ``rxKeys.pendingRxChangesDetailed()`` (prefix match).
 */
export function RxChangeReconcileForm({ rxChangeId, onCompleted }: RxChangeReconcileFormProps) {
  const mutation = useReconcileRxChangeMutation();
  const { form, handleSubmit } = useDetailAction({
    schema: reconcileDraftSchema,
    defaultValues: DEFAULT_VALUES,
    mutation,
    buildVariables: (draft: ReconcileDraft) => ({
      rxChangeId,
      userId: draft.userId.trim(),
      data: {
        referencedPrescriptionId: draft.referencedPrescriptionId.trim(),
      } satisfies ReconcileRxChangeRequestT,
    }),
    successMessage: 'RxChange reconciled.',
    errorTitle: 'Could not reconcile RxChange.',
    logKey: 'reconcile RxChange',
    logContext: { rxChangeId },
    onCompleted,
  });

  return (
    <DetailActionForm
      mutation={mutation}
      onSubmit={handleSubmit}
      submitLabel="Reconcile"
      pendingLabel="Reconciling…"
    >
      <FormField
        form={form}
        type="text"
        name="userId"
        label="Patient user ID"
        description="lotushealth user UUID for the patient on this RxChange."
        placeholder="00000000-0000-0000-0000-000000000000"
        inputProps={{ autoComplete: 'off', spellCheck: false }}
      />
      <FormField
        form={form}
        type="text"
        name="referencedPrescriptionId"
        label="Referenced prescription ID"
        description="DoseSpot prescription to attach this RxChange to."
        placeholder="DoseSpot prescription id"
        inputProps={{ autoComplete: 'off', spellCheck: false }}
      />
    </DetailActionForm>
  );
}
